import {
	FieldMapping,
	FieldMappingSourceType,
	HubspotEntityType,
	HubspotField,
} from "../schema/hubspot";

/**
 * `code` the API sends on the 400 for Lead fields/pipelines when the connected HubSpot account
 * lacks the Leads scopes (PREC-3967). Mirrors HUBSPOT_MISSING_LEAD_SCOPE_CODE in apps/api.
 */
export const HUBSPOT_MISSING_LEAD_SCOPE_CODE = "HUBSPOT_MISSING_LEAD_SCOPE";

interface ApiErrorLike {
	status?: number | string;
	data?: { message?: string | string[]; code?: string };
}

const asApiError = (error: unknown): ApiErrorLike | null =>
	typeof error === "object" && error !== null ? (error as ApiErrorLike) : null;

export const isHubspotMissingLeadScopeError = (error: unknown): boolean =>
	asApiError(error)?.data?.code === HUBSPOT_MISSING_LEAD_SCOPE_CODE;

/** The API's own reason for a rejected request (string or class-validator array), else `fallback`. */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
	const message = asApiError(error)?.data?.message;

	if (Array.isArray(message)) {
		return message.join(", ");
	}

	return typeof message === "string" && message.length > 0 ? message : fallback;
};

const JOINING_WORDS = new Set(["and", "or", "of", "the"]);

/**
 * Mirrors the sync's normalisation (apps/api/src/hubspot/utils/enumeration-value.resolver.ts):
 * lower-case, every non-alphanumeric run is a separator, joining words dropped. The API's alias
 * table for LinkedIn industry names is deliberately not mirrored — a saved literal that only
 * matches through an alias is not a realistic mapping, and the API's verdict wins on save anyway.
 */
export const normaliseOptionToken = (text: string): string =>
	text
		.toLowerCase()
		.split(/[^\p{L}\p{N}]+/u)
		.filter((token) => token.length > 0 && !JOINING_WORDS.has(token))
		.join(" ");

const matchesAnOption = (value: string, options: { label: string; value: string }[]): boolean => {
	const wanted = normaliseOptionToken(value);

	return options.some(
		(option) =>
			option.value === value ||
			normaliseOptionToken(option.value) === wanted ||
			normaliseOptionToken(option.label) === wanted
	);
};

/**
 * A static value mapped onto an enumeration that offers no matching option: HubSpot rejects every
 * record carrying it. Returns the option values to show, or null when the mapping is fine (or the
 * target is free text / unknown / an enumeration without published options).
 */
export const findConstantOptionMismatch = (
	value: string,
	field: HubspotField | undefined
): { options: string[] } | null => {
	if (!field || field.type !== "enumeration" || !field.options?.length) {
		return null;
	}

	const candidates = field.isMulti ? value.split(";").map((part) => part.trim()) : [value];
	const matched = candidates.some((candidate) => matchesAnOption(candidate, field.options ?? []));

	return matched ? null : { options: field.options.map((option) => option.value) };
};

/** Source-field uniqueness only matters in FIELD mode (constants don't consume the picker namespace). */
export const isFieldMode = (sourceType?: FieldMappingSourceType): boolean =>
	(sourceType ?? FieldMappingSourceType.FIELD) === FieldMappingSourceType.FIELD;

export interface PendingMappingLike {
	entityType: HubspotEntityType;
	sourceType: FieldMappingSourceType;
	sourceField: string;
	hubspotField: string;
}

export interface UsedFields {
	usedSourceFields: string[];
	usedHubspotFieldsByType: Record<FieldMappingSourceType, string[]>;
}

/** Per-entity used source fields (FIELD mode only) and per-sourceType used target fields. */
export const buildUsedFields = (
	saved: FieldMapping[] | undefined,
	pending: PendingMappingLike[],
	entity: HubspotEntityType
): UsedFields => {
	const savedFieldSources = (saved ?? [])
		.filter((m) => isFieldMode(m.sourceType) && m.sourceField)
		.map((m) => m.sourceField);
	const pendingOfEntity = pending.filter((m) => m.entityType === entity);
	const pendingFieldSources = pendingOfEntity
		.filter((m) => isFieldMode(m.sourceType) && m.sourceField)
		.map((m) => m.sourceField);

	const byType: Record<FieldMappingSourceType, string[]> = {
		[FieldMappingSourceType.FIELD]: [],
		[FieldMappingSourceType.CONSTANT]: [],
		[FieldMappingSourceType.AI]: [],
	};

	(saved ?? [])
		.filter((m) => m.hubspotField)
		.forEach((m) => byType[m.sourceType ?? FieldMappingSourceType.FIELD].push(m.hubspotField));
	pendingOfEntity
		.filter((m) => m.hubspotField)
		.forEach((m) => byType[m.sourceType].push(m.hubspotField));

	return {
		usedSourceFields: [...savedFieldSources, ...pendingFieldSources],
		usedHubspotFieldsByType: byType,
	};
};
